import { Component, OnInit, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ShortageExplorerService } from './shortage-explorer.service';
import { AppConstants } from '../../../app.constants';
import { ToastMessageService } from '../../../toastr.service';

@Component({
  selector: 'app-shortage-explorer',
  templateUrl: './shortage-explorer.component.html',
  styleUrl: './shortage-explorer.component.scss'
})
export class ShortageExplorerComponent implements OnInit, OnChanges {
  @Input() initialFilters: any = {};

  explorerData: any[] = [];
  filteredData: any[] = []; // Data displayed in table

  lmsFlagFilter: string = '1';
  explorerSearch: string = '';
  statusFilter: string = '';
  updateFilter: string = '';
  routeFilter: string = '';
  routeOptions: string[] = [];
  searchTimeout: any;

  // Pagination
  currentPage: number = 1;
  pageSize: number = 10;
  totalRecords: number = 0;
  totalPages: number = 0;
  isLoading: boolean = false;

  // Modal State
  selectedProduct: any = null;
  showModal: boolean = false;
  expandedNdcs: { [ndc: string]: boolean } = {};
  selectedCompanyFilter: string = 'ALL';

  constructor(
    private shortageService: ShortageExplorerService,
    private toastr: ToastMessageService
  ) { }

  ngOnInit(): void {
    if (this.initialFilters) {
      this.applyInitialFilters();
    }
    this.fetchData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialFilters'] && !changes['initialFilters'].firstChange) {
      this.applyInitialFilters();
      this.fetchData();
    }
  }

  applyInitialFilters(): void {
    this.lmsFlagFilter = this.initialFilters.lmsFlagFilter || '1';
    this.statusFilter = this.initialFilters.statusFilter || '';
    this.routeFilter = '';
    this.explorerSearch = '';
    this.currentPage = 1;
  }

  fetchData(): void {
    this.isLoading = true;
    const email = localStorage.getItem("email") || "";

    const payload: any = {
      user_email: email,
      record_type: this.lmsFlagFilter === "" ? -1 : parseInt(this.lmsFlagFilter, 10),
      page: this.currentPage,
      limit: this.pageSize,
      include_history: true,
      filters: {
        search_query: this.explorerSearch || '',
        status: this.statusFilter || '',
        type_of_update: this.updateFilter || '',
        route: this.routeFilter || '',
        date_from: '',
        date_to: ''
      }
    };

    if (this.initialFilters && this.initialFilters.ids) {
      payload.ids = this.initialFilters.ids || [];
    }

    this.shortageService.getShortageData(payload).subscribe({
      next: (res) => this.handleFetchSuccess(res),
      error: (err) => this.handleFetchError(err)
    });
  }

  private handleFetchSuccess(res: any): void {
    this.explorerData = res.records || [];
    this.totalRecords = res.total_records || 0;
    this.totalPages = res.total_pages || 0;
    if (res.filters_metadata && res.filters_metadata.route_options) {
      this.routeOptions = res.filters_metadata.route_options;
    }
    this.applyStaticFilter();
    this.isLoading = false;
  }

  private handleFetchError(err: any): void {
    console.error("Error fetching shortage data", err);
    this.isLoading = false;
    const errorMsg = err.error?.error || err.error?.message || "Error fetching records";
    this.toastr.error(errorMsg);
  }

  handleFilterChange(): void {
    this.currentPage = 1;
    this.fetchData();
  }

  handleSearchChange(): void {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    this.searchTimeout = setTimeout(() => {
      this.currentPage = 1;
      this.fetchData();
    }, 400);
  }

  applyStaticFilter(): void {
    if (!this.explorerSearch) {
      this.filteredData = [...this.explorerData];
      return;
    }

    const q = this.explorerSearch.toLowerCase();
    this.filteredData = this.explorerData.filter(d => {
      const matchBrand = d.brand_name && d.brand_name.toLowerCase().includes(q);
      const matchNda = d.nda && d.nda.toLowerCase().includes(q);

      const matchNdcs = d.ndcs && d.ndcs.some((ndcItem: any) => {
        const matchNdcNum = ndcItem.ndc && ndcItem.ndc.toLowerCase().includes(q);
        const matchStrength = ndcItem.strength && ndcItem.strength.toLowerCase().includes(q);
        const matchCompany = (ndcItem.company_name && ndcItem.company_name.toLowerCase().includes(q)) ||
          (ndcItem.latest_record?.company_name?.value && ndcItem.latest_record.company_name.value.toLowerCase().includes(q)) ||
          (ndcItem.latest_record?.company_name && typeof ndcItem.latest_record.company_name === 'string' && ndcItem.latest_record.company_name.toLowerCase().includes(q));
        return matchNdcNum || matchStrength || matchCompany;
      });

      return matchBrand || matchNda || matchNdcs;
    });
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.fetchData();
    }
  }

  handlePageSizeChange(): void {
    this.currentPage = 1;
    this.fetchData();
  }

  getVisiblePages(): number[] {
    const pages: number[] = [];
    const maxVisible = 5;
    let start = Math.max(1, this.currentPage - 2);
    let end = Math.min(this.totalPages, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  }

  getNdcsForSelectedProduct(): any[] {
    if (!this.selectedProduct || !this.selectedProduct.ndcs) return [];
    if (this.selectedCompanyFilter === 'ALL') {
      return this.selectedProduct.ndcs;
    }
    return this.selectedProduct.ndcs.filter((ndcItem: any) => {
      return this.getVal(ndcItem.latest_record?.company_name) === this.selectedCompanyFilter;
    });
  }

  getUniqueCompanies(): string[] {
    if (!this.selectedProduct || !this.selectedProduct.ndcs) return [];
    const companySet = new Set<string>();
    this.selectedProduct.ndcs.forEach((ndcItem: any) => {
      const name = this.getVal(ndcItem.latest_record?.company_name);
      if (name && name !== 'N/A') {
        companySet.add(name.trim());
      }
    });
    return Array.from(companySet).sort();
  }

  getNdcCountForCompany(company: string): number {
    if (!this.selectedProduct || !this.selectedProduct.ndcs) return 0;
    return this.selectedProduct.ndcs.filter((ndcItem: any) => {
      return this.getVal(ndcItem.latest_record?.company_name) === company;
    }).length;
  }

  toggleNdcExpand(ndc: string): void {
    this.expandedNdcs[ndc] = !this.expandedNdcs[ndc];
  }

  getVal(fieldObj: any): any {
    if (!fieldObj) return 'N/A';
    if (typeof fieldObj === 'object' && 'value' in fieldObj) {
      return fieldObj.value !== null && fieldObj.value !== undefined ? fieldObj.value : 'N/A';
    }
    return fieldObj;
  }

  getFormattedDate(dateVal: any): string {
    const val = this.getVal(dateVal);
    if (!val || val === 'N/A') return 'N/A';

    // Try parsing the date
    const d = new Date(val);
    if (isNaN(d.getTime())) {
      return val; // Return original string if not a valid date
    }

    // Format to dd-MM-yyyy
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  }

  isPending(ndcItem: any): boolean {
    const lms = ndcItem.lms_flag !== undefined ? ndcItem.lms_flag : ndcItem.latest_record?.lms_flag;
    if (lms !== undefined) {
      return lms === 0;
    }
    return this.lmsFlagFilter === '0' || this.getVal(ndcItem.latest_record?.status) === 'Pending';
  }

  showFieldChangesModal: boolean = false;
  selectedFieldChanges: any[] = [];
  selectedFieldName: string = '';
  selectedFieldNdc: string = '';

  getFieldChanges(ndcItem: any, fieldName: string): any[] {
    if (!ndcItem || !ndcItem.changes) return [];
    const norm = fieldName.toLowerCase().replace(/_/g, '');
    return ndcItem.changes.filter((c: any) => {
      if (!c.field) return false;
      const cNorm = c.field.toLowerCase().replace(/_/g, '');
      if (norm === cNorm) return true;
      if (norm === 'reasonforshortage' && cNorm === 'reasonforshortage') return true;
      if (norm === 'reasonforshortage' && cNorm === 'reasonofshortage') return true;
      if (norm === 'datediscontinued' && cNorm === 'datediscontinuation') return true;
      return false;
    });
  }

  openFieldChanges(ndcItem: any, fieldName: string, displayName: string, event: Event): void {
    event.stopPropagation();
    this.selectedFieldNdc = ndcItem.ndc || 'N/A';
    this.selectedFieldName = displayName;
    this.selectedFieldChanges = this.getFieldChanges(ndcItem, fieldName);
    this.showFieldChangesModal = true;
  }

  closeFieldChangesModal(): void {
    this.showFieldChangesModal = false;
    this.selectedFieldChanges = [];
    this.selectedFieldName = '';
    this.selectedFieldNdc = '';
  }

  onOpenDetail(product: any): void {
    this.openModal(product);
  }

  openModal(product: any): void {
    this.selectedProduct = product;
    this.selectedCompanyFilter = 'ALL';
    this.expandedNdcs = {};
    if (product && product.ndcs && product.ndcs.length > 0) {
      const firstNdc = product.ndcs[0].ndc;
      if (firstNdc) {
        this.expandedNdcs[firstNdc] = true;
      }
    }
    this.showModal = true;
  }

  closeModal(): void {
    this.showModal = false;
    this.selectedProduct = null;
    this.expandedNdcs = {};
  }

  onRerun(record: any): void {
    if (record.isRerunning) return;

    record.isRerunning = true;
    const payload = {
      ndc: record.ndc,
      user_email: localStorage.getItem("email") || "",
      nda: this.selectedProduct ? this.selectedProduct.nda : ""
    };

    this.shortageService.rerunNdc(payload).subscribe({
      next: (res) => {
        this.toastr.success(res.message || "NDC reprocessed successfully");
        record.isRerunning = false;
      },
      error: (err) => {
        console.error("Error rerunning NDC", err);
        const errorMsg = err.error?.error || err.error?.message || "Something went wrong";
        this.toastr.error(errorMsg);
        record.isRerunning = false;
      }
    });
  }
}
